import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Actions over a multi-row selection.
 *
 * The absence of this bar is why 203 mislinked accounts had to be repaired by a
 * script instead of by the accountant. It appears only when something is
 * selected, so the default view stays quiet.
 */

interface BulkActionBarProps {
  selectedIds: string[];
  onClear: () => void;
  onDone: () => void;
  onDeleted: (ids: string[]) => void;
}

type PendingAction = 'parent' | 'type' | null;

const TYPES = [
  { value: 'ASSET', labelKey: 'assets' },
  { value: 'LIABILITY', labelKey: 'liabilities' },
  { value: 'EQUITY', labelKey: 'equity' },
  { value: 'INCOME', labelKey: 'income' },
  { value: 'EXPENSE', labelKey: 'expenses' },
] as const;

export function BulkActionBar({ selectedIds, onClear, onDone, onDeleted }: BulkActionBarProps) {
  const { t } = useTranslation('accounting');
  const [pending, setPending] = useState<PendingAction>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canDelete = usePermission('ACCOUNTING', 'DELETE');
  const utils = trpc.useUtils();

  // Only leaves are offered as a parent target: a header account's own balance
  // is not where postings live, and nesting under one is the common mistake.
  const { data: candidates } = trpc.account.list.useQuery(
    { limit: 1000 },
    { enabled: pending === 'parent' }
  );

  const bulkUpdate = trpc.accountWorkbench.bulkUpdate.useMutation({
    onSuccess: (result) => {
      utils.account.getTree.invalidate();
      utils.account.list.invalidate();
      utils.accountWorkbench.health.invalidate();
      utils.accountWorkbench.changes.invalidate();
      setPending(null);
      setWarnings(result.warnings);
      if (result.warnings.length === 0) onDone();
    },
  });

  const bulkDelete = trpc.account.bulkDelete.useMutation({
    onSuccess: (result) => {
      utils.account.invalidate();
      utils.accountWorkbench.health.invalidate();
      setDeleteOpen(false);
      onDeleted(result.deletedIds);
      setWarnings([
        t('bulkActions.deleted', { count: result.deletedCount }),
        ...result.skipped.map((row) => `${row.code}: ${row.reason}`),
      ]);
    },
  });

  if (selectedIds.length === 0 && warnings.length === 0) return null;

  const apply = (payload: Record<string, unknown>) =>
    bulkUpdate.mutate({ ids: selectedIds, ...payload } as Parameters<typeof bulkUpdate.mutate>[0]);

  return (
    <div className="border-primary/30 bg-primary/5 border">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
        <span className="text-sm font-medium">
          {t('bulkActions.selected', { count: selectedIds.length })}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {pending === null && selectedIds.length > 0 && (
            <>
              <Button variant="outline" className="h-8" onClick={() => setPending('parent')}>
                {t('bulkActions.changeParent')}
              </Button>
              <Button variant="outline" className="h-8" onClick={() => setPending('type')}>
                {t('bulkActions.changeType')}
              </Button>
              <Button
                variant="outline"
                className="h-8"
                disabled={bulkUpdate.isPending}
                onClick={() => apply({ isActive: false })}
              >
                {t('bulkActions.deactivate')}
              </Button>
              <Button
                variant="outline"
                className="h-8"
                disabled={bulkUpdate.isPending}
                onClick={() => apply({ isActive: true })}
              >
                {t('bulkActions.activate')}
              </Button>
              {canDelete && (
                <Button
                  variant="destructive"
                  className="h-8"
                  disabled={bulkUpdate.isPending || bulkDelete.isPending}
                  onClick={() => {
                    bulkDelete.reset();
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('bulkActions.delete')}
                </Button>
              )}
            </>
          )}

          {pending === 'parent' && (
            <div className="flex items-center gap-2">
              <Combobox
                options={[
                  { value: '__root__', label: t('bulkActions.makeTopLevel') },
                  ...(candidates?.accounts ?? [])
                    .filter((account) => !selectedIds.includes(account.id))
                    .map((account) => ({
                      value: account.id,
                      label: `${account.code} - ${account.name}`,
                    })),
                ]}
                value=""
                onValueChange={(value) => apply({ parentId: value === '__root__' ? null : value })}
                placeholder={t('bulkActions.selectParent')}
                searchPlaceholder={t('accountModal.searchAccounts')}
              />
              <Button variant="ghost" className="h-8" onClick={() => setPending(null)}>
                {t('accountModal.cancel')}
              </Button>
            </div>
          )}

          {pending === 'type' && (
            <div className="flex items-center gap-2">
              <Combobox
                options={TYPES.map((type) => ({
                  value: type.value,
                  label: t(`chartOfAccounts.${type.labelKey}`),
                }))}
                value=""
                onValueChange={(value) => apply({ type: value })}
                placeholder={t('accountModal.selectType')}
                searchPlaceholder={t('accountModal.searchAccounts')}
              />
              <Button variant="ghost" className="h-8" onClick={() => setPending(null)}>
                {t('accountModal.cancel')}
              </Button>
            </div>
          )}

          {bulkUpdate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}

          <button
            onClick={() => {
              setWarnings([]);
              setPending(null);
              onClear();
            }}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label={t('bulkActions.clearSelection')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {bulkUpdate.error && (
        <p className="border-border border-t bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {bulkUpdate.error.message}
        </p>
      )}

      {warnings.length > 0 && (
        <div className="border-border border-t bg-amber-50 px-4 py-2 dark:bg-amber-950/30">
          <ul className="list-inside list-disc space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <button
            className="mt-1.5 text-xs font-medium text-amber-900 underline dark:text-amber-200"
            onClick={() => {
              setWarnings([]);
              onDone();
            }}
          >
            {t('accountModal.acknowledge')}
          </button>
        </div>
      )}

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!bulkDelete.isPending) setDeleteOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulkActions.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('bulkActions.deleteDescription', { count: selectedIds.length })}
            </DialogDescription>
          </DialogHeader>
          {bulkDelete.error && <p className="text-sm text-red-700">{bulkDelete.error.message}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={bulkDelete.isPending}
              onClick={() => setDeleteOpen(false)}
            >
              {t('accountModal.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={bulkDelete.isPending}
              onClick={() => bulkDelete.mutate({ ids: selectedIds })}
            >
              {bulkDelete.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('bulkActions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
